import React from 'react';
import _ from 'lodash';
import cn from 'classnames';

import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import Cancel from '@mui/icons-material/Cancel';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import { isInternalUser } from 'states/profile/business-rules';
import { SortableChipRow } from 'widgets-v5/chip';
import { SearchInput } from 'widgets-v6/standard-input';

export const MAX_V4_DIMENSIONS = 9;

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        display: 'flex',
    },
    list: {
        flexGrow: 1,
        height: '100%',
    },
    tabs: {
        borderRight: `1px solid ${theme.palette.divider}`,
        minWidth: 200,
    },
    tabPanel: {
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    subcategory: {
        paddingLeft: theme.spacing(4),
    },
    selectedGroup: {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        boxShadow: `inset -3px 0 0 ${theme.palette.primary.main}`,
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
        },
    },
    expand: {
        transform: 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
    chipLayer: {
        position: 'relative',
        zIndex: 2000, // To ensure the chip always above the drawer/dialog
        overflow: 'visible',
    },
    chipLayerInner: {
        overflow: 'auto',
        paddingBottom: 4,
        // Firefox
        scrollbarWidth: 'auto',
        scrollbarColor: 'rgba(0,0,0,0.35) transparent',

        // WebKit (Chrome/Safari/Edge)
        '&::-webkit-scrollbar': {
            width: 10,
            height: 10,
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.35)',
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0,0,0,0.5)',
        },
    },
    scrollBox: {
        overflow: 'auto',

        // Firefox
        scrollbarWidth: 'auto',
        scrollbarColor: 'rgba(0,0,0,0.35) transparent',

        // WebKit (Chrome/Safari/Edge)
        '&::-webkit-scrollbar': {
            width: 10,
            height: 10,
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.35)',
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0,0,0,0.5)',
        },
    },
}));

function useElementHeight(ref, deps = []) {
    const [height, setHeight] = React.useState(0);

    React.useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;

        const update = () => {
            const rect = el.getBoundingClientRect();
            if (!rect) return;

            // Use Math.round to ignore sub-pixel jitter (e.g., 500.001px vs 500px)
            const next = Math.round(rect.height);

            setHeight(prev => {
                // Only update if the integer value is different
                return prev !== next ? next : prev;
            });
        };

        const ro = new ResizeObserver(() => {
            // Decouple the resize event from the current render cycle
            window.requestAnimationFrame(() => {
                update();
            });
        });

        ro.observe(el);
        update(); // Initial measure

        window.addEventListener('resize', update);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', update);
        };
    }, deps);

    return height;
}

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

export function toggleV4Dimension(values, value) {
    if (_.includes(values, value)) {
        return _.filter(values, selectedValue => selectedValue !== value);
    }

    return values.length >= MAX_V4_DIMENSIONS ? values : _.concat(values, value);
}

export function isV4DimensionDisabled(values, value) {
    return values.length >= MAX_V4_DIMENSIONS && !_.includes(values, value);
}

export function getV4GroupSelectionCount(dimension, values) {
    return _.filter(dimension.attributes || [], attribute => _.includes(values, attribute.value))
        .length;
}

export function getV4DimensionItems(dimensionCategories) {
    return _(dimensionCategories)
        .flatMap(category =>
            _.flatMap(category.dimensions || [], dimension => {
                if (!_.size(dimension.attributes)) {
                    return [{ ...dimension, categoryLabel: category.label }];
                }

                return _.map(dimension.attributes, attribute => ({
                    ...attribute,
                    categoryLabel: category.label,
                    label: attribute.label.toLowerCase().startsWith(dimension.label.toLowerCase())
                        ? attribute.label
                        : `${dimension.label} ${attribute.label}`,
                }));
            })
        )
        .value();
}

export function getSelectedV4DimensionItems(dimensionItems, values) {
    const itemsByValue = _.keyBy(dimensionItems, 'value');
    return _.compact(_.map(values, value => itemsByValue[value]));
}

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
        >
            {value === index && children}
        </div>
    );
}

export function DimensionsSelector(props) {
    const {
        dimensionCategories = [],
        dimensionsMapping = {},
        dimensionsValues = [],
        attributesValues = [],
        dimensionsOnChange = () => {},
        attributesOnChange = () => {},
        errors = {},
        showErrors = false,
        ownOrganization,
        chipItems,
        chipValue,
        chipOnChange,
        chipLabel = 'Split on:',
        showChips = true,
        isV4 = false,
    } = props;

    const classes = useStyles();
    const [activeTab, setActiveTab] = React.useState(0);
    const [expandedDim, setExpandedDim] = React.useState('');
    const [searchQuery, setSearchQuery] = React.useState('');
    const [searchTerm, setSearchTerm] = React.useState('');
    const tabsRef = React.useRef(null);
    const tabsHeight = useElementHeight(tabsRef, [dimensionCategories?.length]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const toggleAttributions = val => {
        setExpandedDim(prev => (prev === val ? '' : val));
    };

    const debouncedFilter = React.useMemo(
        () =>
            _.debounce(nextValue => {
                setSearchTerm(nextValue);
            }, 300),
        []
    );

    const v4DimensionItems = React.useMemo(() => getV4DimensionItems(dimensionCategories), [
        dimensionCategories,
    ]);

    const filteredCategories = React.useMemo(() => {
        if (!searchTerm) return dimensionCategories;

        const lowerSearch = searchTerm.toLowerCase();
        const matchingV4Values = isV4
            ? new Set(
                  _.map(
                      _.filter(v4DimensionItems, item =>
                          item.label.toLowerCase().includes(lowerSearch)
                      ),
                      'value'
                  )
              )
            : null;

        return dimensionCategories
            .map(category => {
                if (isV4) {
                    const filteredDimensions = (category.dimensions || [])
                        .filter(dimension =>
                            _.size(dimension.attributes)
                                ? _.some(dimension.attributes, attribute =>
                                      matchingV4Values.has(attribute.value)
                                  )
                                : matchingV4Values.has(dimension.value)
                        )
                        .map(dimension =>
                            _.size(dimension.attributes)
                                ? {
                                      ...dimension,
                                      attributes: dimension.attributes.filter(attribute =>
                                          matchingV4Values.has(attribute.value)
                                      ),
                                  }
                                : dimension
                        );

                    return { ...category, dimensions: filteredDimensions };
                }

                const filteredDimensions = (category.dimensions || [])
                    .map(dim => {
                        const dimMatches = dim.label.toLowerCase().includes(lowerSearch);

                        // Filter the attributes themselves
                        const filteredAttributes = (dim.attributes || []).filter(attr =>
                            attr.label.toLowerCase().includes(lowerSearch)
                        );

                        // If the dimension label matches, we usually want to show all attributes.
                        // If it doesn't match, we only show the attributes that matched the search.
                        return {
                            ...dim,
                            attributes: dimMatches ? dim.attributes : filteredAttributes,
                        };
                    })
                    .filter(dim => {
                        // Keep the dimension if its label matches OR it has matching attributes
                        return (
                            dim.label.toLowerCase().includes(lowerSearch) ||
                            (dim.attributes && dim.attributes.length > 0)
                        );
                    });

                return { ...category, dimensions: filteredDimensions };
            })
            .filter(category => category.dimensions.length > 0);
    }, [dimensionCategories, isV4, searchTerm, v4DimensionItems]);

    const checkedValues = React.useMemo(() => {
        const m = {};
        _.each(dimensionsValues, v => (m[v] = true));
        return m;
    }, [dimensionsValues]);

    const checkedAttributesValues = React.useMemo(() => {
        const m = {};
        _.each(attributesValues, v => (m[v] = true));
        return m;
    }, [attributesValues]);

    const allDimensions = React.useMemo(() => {
        return _(dimensionCategories)
            .map(category => category.dimensions || [])
            .flatten()
            .value();
    }, [dimensionCategories]);

    const selectedV4Items = getSelectedV4DimensionItems(v4DimensionItems, dimensionsValues);

    const selectedCount = dimensionsValues.length;

    const getDisabledReason = (dimension, value) => {
        if (dimension.isBlocked) {
            return dimension.blockedReason || 'Data is only available after April 1, 2023';
        }

        if (isV4 && value && isV4DimensionDisabled(dimensionsValues, value)) {
            return `Maximum ${MAX_V4_DIMENSIONS} dimensions selected`;
        }

        return '';
    };

    React.useEffect(() => {
        if (searchTerm) {
            if (filteredCategories[activeTab]) {
                const firstDimWithMatchingAttr = filteredCategories[activeTab].dimensions.find(
                    dim =>
                        (dim.attributes || []).some(attr =>
                            attr.label.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                );
                if (firstDimWithMatchingAttr) {
                    setExpandedDim(firstDimWithMatchingAttr.value);
                }
            }
            if (activeTab >= filteredCategories.length) setActiveTab(0);
        }
    }, [searchTerm, filteredCategories]);

    const handleSearchChange = event => {
        const nextValue = event.target.value;
        setSearchQuery(nextValue); // 1. Update the text box immediately
        if (isV4) {
            setSearchTerm(nextValue);
        } else {
            debouncedFilter(nextValue); // Preserve the legacy delayed filter
        }
    };

    const handleReset = () => {
        setSearchQuery('');
        setSearchTerm('');
        setExpandedDim('');
        debouncedFilter.cancel(); // Cancel any pending debounce
    };

    const changeDimensions = dimValue => {
        if (isV4) {
            dimensionsOnChange(toggleV4Dimension(dimensionsValues, dimValue));
            return;
        }

        const existingDimensions = dimensionsValues;
        const selectedAttributes = attributesValues;

        const currentDimension = _.find(allDimensions, d => d.value === dimValue);

        if (!currentDimension) return;

        const attributesInDimension = _.map(currentDimension.attributes || [], a => a.value);

        if (_.includes(existingDimensions, dimValue)) {
            const newDimensions = _.filter(existingDimensions, d => d !== dimValue);
            const newAttributes = _.filter(
                selectedAttributes,
                a => !_.includes(attributesInDimension, a)
            );

            dimensionsOnChange(newDimensions);
            attributesOnChange(newAttributes);
        } else {
            dimensionsOnChange(_.concat(existingDimensions, dimValue));
        }
    };

    const changeAttributions = attrValue => {
        if (isV4) {
            dimensionsOnChange(toggleV4Dimension(dimensionsValues, attrValue));
            return;
        }

        const existingAttributes = attributesValues;
        const existingDimensions = dimensionsValues;

        // find which dimension owns this attribute
        let attributeDimension = '';
        _.forEach(dimensionCategories, category => {
            _.forEach(category.dimensions || [], dimension => {
                const attrs = _.map(dimension.attributes || [], a => a.value);
                if (_.includes(attrs, attrValue)) attributeDimension = dimension.value;
            });
        });

        if (!attributeDimension) return;

        const isDimensionSelected = _.includes(existingDimensions, attributeDimension);

        if (_.includes(existingAttributes, attrValue)) {
            attributesOnChange(_.filter(existingAttributes, a => a !== attrValue));
        } else {
            attributesOnChange(_.concat(existingAttributes, attrValue));
            if (!isDimensionSelected) changeDimensions(attributeDimension);
        }
    };

    return (
        <React.Fragment>
            <Paper className={classes.root}>
                <Grid container>
                    <Grid item xs={12}>
                        <Box
                            p={1}
                            borderBottom={1}
                            borderColor="divider"
                            display="flex"
                            alignItems="center"
                        >
                            <Box flexGrow={1}>
                                <SearchInput
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    onReset={handleReset}
                                    placeholder={
                                        isV4
                                            ? 'Filter dimensions...'
                                            : 'Filter dimensions or attributes...'
                                    }
                                    defaultValue={searchQuery}
                                />
                            </Box>
                            {isV4 && (
                                <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                                    {selectedCount} / {MAX_V4_DIMENSIONS} selected
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={7} className={classes.tabPanel}>
                        <Box display="flex" width="100%">
                            <Tabs
                                ref={tabsRef}
                                orientation="vertical"
                                value={activeTab}
                                onChange={handleTabChange}
                                className={classes.tabs}
                                indicatorColor="primary"
                                textColor="primary"
                            >
                                {_.map(filteredCategories, (dimensionCat, index) => (
                                    <Tab
                                        key={dimensionCat.value}
                                        label={dimensionCat.label}
                                        {...a11yProps(index)}
                                    />
                                ))}
                            </Tabs>

                            {/* LEFT: dimensions list height matches Tabs */}
                            <Box
                                className={classes.scrollBox}
                                height={tabsHeight || 'auto'}
                                width="100%"
                            >
                                {filteredCategories.length === 0 && (
                                    <Box p={3} textAlign="center">
                                        <Typography color="textSecondary">
                                            No dimensions match “{searchQuery}”
                                        </Typography>
                                    </Box>
                                )}

                                {_.map(filteredCategories, (dimensionCat, index) => (
                                    <TabPanel
                                        key={dimensionCat.value}
                                        value={activeTab}
                                        index={index}
                                    >
                                        <List className={classes.list}>
                                            {_.map(dimensionCat.dimensions || [], dimension => (
                                                <React.Fragment key={dimension.value}>
                                                    <Tooltip
                                                        title={getDisabledReason(
                                                            dimension,
                                                            _.size(dimension.attributes)
                                                                ? null
                                                                : dimension.value
                                                        )}
                                                    >
                                                        <ListItem
                                                            className={cn({
                                                                [classes.selectedGroup]:
                                                                    isV4 &&
                                                                    _.size(dimension.attributes) >
                                                                        0 &&
                                                                    getV4GroupSelectionCount(
                                                                        dimension,
                                                                        dimensionsValues
                                                                    ) > 0,
                                                            })}
                                                            disabled={
                                                                (!isV4 && dimension.isBlocked) ||
                                                                (isV4 &&
                                                                    !_.size(dimension.attributes) &&
                                                                    !!getDisabledReason(
                                                                        dimension,
                                                                        dimension.value
                                                                    ))
                                                            }
                                                            dense
                                                            button
                                                            onClick={() => {
                                                                return isV4 &&
                                                                    _.size(dimension.attributes)
                                                                    ? toggleAttributions(
                                                                          dimension.value
                                                                      )
                                                                    : changeDimensions(
                                                                          dimension.value
                                                                      );
                                                            }}
                                                        >
                                                            {(!isV4 ||
                                                                !_.size(dimension.attributes)) && (
                                                                <ListItemIcon>
                                                                    <Checkbox
                                                                        color="primary"
                                                                        edge="start"
                                                                        checked={
                                                                            !!checkedValues[
                                                                                dimension.value
                                                                            ]
                                                                        }
                                                                        tabIndex={-1}
                                                                        disableRipple
                                                                        disabled={
                                                                            dimension.isBlocked ||
                                                                            (isV4 &&
                                                                                isV4DimensionDisabled(
                                                                                    dimensionsValues,
                                                                                    dimension.value
                                                                                ))
                                                                        }
                                                                        inputProps={{
                                                                            'aria-labelledby':
                                                                                dimension.value,
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                            )}

                                                            <ListItemText
                                                                id={dimension.value}
                                                                primary={dimension.label}
                                                                secondary={
                                                                    isV4 &&
                                                                    _.size(dimension.attributes)
                                                                        ? `Group - ${getV4GroupSelectionCount(
                                                                              dimension,
                                                                              dimensionsValues
                                                                          )} selected`
                                                                        : 'Dimension'
                                                                }
                                                            />

                                                            {_.size(dimension.attributes) > 0 && (
                                                                <ListItemSecondaryAction>
                                                                    <IconButton
                                                                        edge="end"
                                                                        onClick={event => {
                                                                            event.stopPropagation();
                                                                            toggleAttributions(
                                                                                dimension.value
                                                                            );
                                                                        }}
                                                                        className={cn(
                                                                            classes.expand,
                                                                            {
                                                                                [classes.expandOpen]:
                                                                                    expandedDim ===
                                                                                    dimension.value,
                                                                            }
                                                                        )}
                                                                    >
                                                                        <ExpandMoreIcon />
                                                                    </IconButton>
                                                                </ListItemSecondaryAction>
                                                            )}
                                                        </ListItem>
                                                    </Tooltip>

                                                    <Collapse
                                                        in={expandedDim === dimension.value}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <List>
                                                            {_.map(
                                                                dimension.attributes || [],
                                                                attribution => {
                                                                    if (
                                                                        attribution.value ===
                                                                            'partner_attr' ||
                                                                        attribution.value ===
                                                                            'sales_rep_attr'
                                                                    ) {
                                                                        return (
                                                                            _.includes(
                                                                                ['admin'],
                                                                                _.get(
                                                                                    ownOrganization,
                                                                                    'type'
                                                                                )
                                                                            ) &&
                                                                            isInternalUser() && (
                                                                                <DimensionsSelectorListItem
                                                                                    key={
                                                                                        attribution.value
                                                                                    }
                                                                                    attribution={
                                                                                        attribution
                                                                                    }
                                                                                    dimension={
                                                                                        dimension
                                                                                    }
                                                                                    checkedAttributesValues={
                                                                                        isV4
                                                                                            ? checkedValues
                                                                                            : checkedAttributesValues
                                                                                    }
                                                                                    changeAttributions={
                                                                                        changeAttributions
                                                                                    }
                                                                                    isV4={isV4}
                                                                                    selectionDisabled={
                                                                                        isV4 &&
                                                                                        isV4DimensionDisabled(
                                                                                            dimensionsValues,
                                                                                            attribution.value
                                                                                        )
                                                                                    }
                                                                                />
                                                                            )
                                                                        );
                                                                    }

                                                                    return (
                                                                        <DimensionsSelectorListItem
                                                                            key={attribution.value}
                                                                            attribution={
                                                                                attribution
                                                                            }
                                                                            dimension={dimension}
                                                                            checkedAttributesValues={
                                                                                isV4
                                                                                    ? checkedValues
                                                                                    : checkedAttributesValues
                                                                            }
                                                                            changeAttributions={
                                                                                changeAttributions
                                                                            }
                                                                            isV4={isV4}
                                                                            selectionDisabled={
                                                                                isV4 &&
                                                                                isV4DimensionDisabled(
                                                                                    dimensionsValues,
                                                                                    attribution.value
                                                                                )
                                                                            }
                                                                        />
                                                                    );
                                                                }
                                                            )}
                                                        </List>
                                                    </Collapse>
                                                </React.Fragment>
                                            ))}
                                        </List>
                                    </TabPanel>
                                ))}
                            </Box>
                        </Box>
                    </Grid>

                    {/* RIGHT: selected panel height matches Tabs, with internal scroll */}
                    <Grid item xs={5}>
                        <Box
                            p={2}
                            height={tabsHeight || 'auto'}
                            display="flex"
                            flexDirection="column"
                        >
                            <Typography variant="body1" gutterBottom>
                                {isV4 ? `Selected dimensions (${selectedCount})` : 'Selected'}
                            </Typography>

                            <Box ml={2} overflow="auto" flex={1} className={classes.scrollBox}>
                                <List dense>
                                    {isV4 && selectedV4Items.length === 0 && (
                                        <Box py={3} textAlign="center">
                                            <Typography color="textSecondary" variant="body2">
                                                Select 1–{MAX_V4_DIMENSIONS} dimensions to define
                                                how report results are grouped.
                                            </Typography>
                                        </Box>
                                    )}
                                    {isV4
                                        ? _.map(selectedV4Items, (item, index) => (
                                              <ListItem key={item.value}>
                                                  <ListItemText
                                                      primary={`${index + 1}. ${item.label}`}
                                                      secondary={item.categoryLabel}
                                                  />
                                                  <ListItemSecondaryAction>
                                                      <IconButton
                                                          edge="end"
                                                          size="small"
                                                          aria-label={`Remove ${item.label}`}
                                                          onClick={() =>
                                                              dimensionsOnChange(
                                                                  toggleV4Dimension(
                                                                      dimensionsValues,
                                                                      item.value
                                                                  )
                                                              )
                                                          }
                                                      >
                                                          <CloseIcon fontSize="small" />
                                                      </IconButton>
                                                  </ListItemSecondaryAction>
                                              </ListItem>
                                          ))
                                        : _.map(dimensionCategories, category => {
                                              const selectedDimensions = _.filter(
                                                  category.dimensions,
                                                  dimension =>
                                                      _.includes(dimensionsValues, dimension.value)
                                              );
                                              return selectedDimensions.length ? (
                                                  <React.Fragment key={category.label}>
                                                      <Typography color="textSecondary">
                                                          {category.label}:
                                                      </Typography>
                                                      <Box ml={1} overflow="auto">
                                                          {_.map(selectedDimensions, dimension => {
                                                              const dimMeta = _.get(
                                                                  dimensionsMapping,
                                                                  dimension.value
                                                              );
                                                              const dimLabel = _.get(
                                                                  dimMeta,
                                                                  'label',
                                                                  dimension.value
                                                              );

                                                              return (
                                                                  <React.Fragment
                                                                      key={dimension.value}
                                                                  >
                                                                      <ListItem>
                                                                          <ListItemText
                                                                              primary={dimLabel}
                                                                              secondary="Dimension"
                                                                          />
                                                                      </ListItem>

                                                                      <Box ml={2}>
                                                                          {_.map(
                                                                              attributesValues,
                                                                              attribute => {
                                                                                  const attrLabel = _.get(
                                                                                      dimMeta,
                                                                                      [
                                                                                          'attributes',
                                                                                          attribute,
                                                                                      ]
                                                                                  );
                                                                                  if (!attrLabel)
                                                                                      return null;

                                                                                  return (
                                                                                      <ListItem
                                                                                          key={
                                                                                              attribute
                                                                                          }
                                                                                      >
                                                                                          <ListItemText
                                                                                              primary={
                                                                                                  attrLabel
                                                                                              }
                                                                                              secondary="Attribute"
                                                                                          />
                                                                                      </ListItem>
                                                                                  );
                                                                              }
                                                                          )}
                                                                      </Box>
                                                                  </React.Fragment>
                                                              );
                                                          })}
                                                      </Box>
                                                  </React.Fragment>
                                              ) : null;
                                          })}
                                </List>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {showChips && (isV4 || chipItems) && chipValue && chipOnChange ? (
                <Box mt={2} className={classes.chipLayer}>
                    {chipValue.length > 0 && (
                        <Grid container direction={'column'} mb={1} gap={1}>
                            <Alert severity="info" sx={{ alignItems: 'center' }}>
                                You can drag and drop the chips to reorder the splits, or click the{' '}
                                {/* Decorative copy of the chip close icon. Not clickable. */}
                                <Cancel
                                    sx={{
                                        fontSize: '1em',
                                        color: theme => alpha(theme.palette.text.primary, 0.26),
                                        verticalAlign: 'middle',
                                        cursor: 'text',
                                    }}
                                />{' '}
                                icon to remove a split.
                            </Alert>
                            <Typography variant="body1">{chipLabel}</Typography>
                        </Grid>
                    )}
                    <Box className={classes.chipLayerInner} overflow="auto">
                        <SortableChipRow
                            items={isV4 ? v4DimensionItems : chipItems}
                            value={chipValue}
                            onChange={chipOnChange}
                            wrap={isV4}
                        />
                    </Box>
                </Box>
            ) : null}

            {showErrors && (
                <div>
                    <FormHelperText error>{errors.dimensions}</FormHelperText>
                    {!isV4 && <FormHelperText error>{errors.attributes}</FormHelperText>}
                </div>
            )}
        </React.Fragment>
    );
}

function DimensionsSelectorListItem(props) {
    const {
        attribution,
        dimension,
        checkedAttributesValues,
        changeAttributions,
        isV4,
        selectionDisabled,
    } = props;
    const isDisabled = dimension.isBlocked || selectionDisabled;
    const classes = useStyles();

    return (
        <ListItem
            disabled={isDisabled}
            className={classes.subcategory}
            dense
            button
            onClick={() => changeAttributions(attribution.value)}
        >
            <ListItemIcon>
                <Checkbox
                    color="primary"
                    edge="start"
                    checked={!!checkedAttributesValues[attribution.value]}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': attribution.value }}
                    disabled={isDisabled}
                />
            </ListItemIcon>

            <ListItemText
                id={attribution.value}
                primary={attribution.label}
                secondary={
                    isV4
                        ? dimension.blockedReason ||
                          (dimension.isBlocked
                              ? 'Data is only available after April 1, 2023'
                              : selectionDisabled
                              ? `Maximum ${MAX_V4_DIMENSIONS} dimensions selected`
                              : 'Dimension')
                        : attribution.category === 'custom'
                        ? 'Attribute - Custom'
                        : 'Attribute'
                }
            />
        </ListItem>
    );
}
